import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { startCase } from 'es-toolkit/compat';
import React from 'react';

import { ResultType } from 'interfaces/experimentResults';
import { JobExperimentResultApprovalBreakdown } from 'interfaces/jobExperimentResults';
import { formatDateTimeForUser } from 'utils/dateTime';
import { useJobExperimentResultDetail } from 'utils/queryHooks/jobs/useJobExperimentResults';

interface JobExperimentResultsSectionProps {
  jobId: string;
  showSummary?: boolean;
  showResultDetails?: boolean;
  slideId?: string;
}

const inferenceResultTypeLabels: Partial<Record<ResultType, string>> = {
  [ResultType.SlideInferenceAreas]: 'Area inference',
  [ResultType.SlideInferenceCellSegmentation]: 'Cell inference',
};

const ApprovalBreakdown: React.FC<{
  label: string;
  breakdown: JobExperimentResultApprovalBreakdown;
}> = ({ label, breakdown }) => (
  <Box>
    <Typography variant="subtitle2">{label}</Typography>
    <Typography variant="body2" color="text.secondary">
      Approved {breakdown.trueCount} | Not approved {breakdown.falseCount} | Unset {breakdown.unsetCount}
    </Typography>
  </Box>
);

export const JobExperimentResultsSection: React.FC<JobExperimentResultsSectionProps> = ({
  jobId,
  showSummary = true,
  showResultDetails = true,
  slideId,
}) => {
  const { data: summary, error, isLoading, refetch } = useJobExperimentResultDetail(jobId);
  const results = summary?.results.filter((result) => !slideId || result.slideId === slideId) || [];
  const hasVisibleResults = showSummary ? Boolean(summary && summary.totalResultCount > 0) : results.length > 0;

  return (
    <Stack component="section" spacing={2} aria-labelledby="job-experiment-results-heading">
      <Divider />
      <Typography id="job-experiment-results-heading" variant="h5">
        Experiment Results
      </Typography>

      {isLoading ? (
        <Stack spacing={1} aria-label="Loading experiment results">
          <Skeleton variant="text" width="55%" />
          <Skeleton variant="rectangular" height={96} />
        </Stack>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load experiment results.
        </Alert>
      ) : !summary || !hasVisibleResults ? (
        <Typography variant="body2" color="text.secondary">
          {slideId && summary?.totalResultCount
            ? 'No experiment results for this slide.'
            : 'No related experiment results.'}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {showSummary && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: 1.5,
                }}
              >
                {[
                  ['Total results', summary.totalResultCount],
                  ['Active', summary.activeResultCount],
                  ['Archived', summary.archivedResultCount],
                  ['Slides', summary.slideCount],
                  ['Heatmap results', summary.heatmapResultCount],
                  ['Feature results', summary.featureResultCount],
                  ['Feature values', summary.featureValueCount],
                ].map(([label, value]) => (
                  <Box key={label}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h6">{value}</Typography>
                  </Box>
                ))}
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <ApprovalBreakdown label="Approval" breakdown={summary.approval} />
                <ApprovalBreakdown label="Internal approval" breakdown={summary.internalApproval} />
              </Stack>
            </>
          )}

          {showResultDetails && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Result details
              </Typography>
              <List dense disablePadding>
                {results.map((result) => {
                  const createdAt = formatDateTimeForUser(result.createdAt);
                  const archivedAt = formatDateTimeForUser(result.deletedAt);
                  const resultTypeLabel = result.resultType
                    ? inferenceResultTypeLabels[result.resultType as ResultType] || startCase(result.resultType)
                    : undefined;
                  const metadata = [resultTypeLabel, result.flowClassName ? startCase(result.flowClassName) : undefined]
                    .filter(Boolean)
                    .join(' | ');
                  return (
                    <ListItem key={result.experimentResultId} disableGutters divider alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" fontWeight={600}>
                              Result {result.experimentResultId}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={result.deletedAt ? 'default' : 'success'}
                              label={result.deletedAt ? 'Archived' : 'Active'}
                            />
                          </Stack>
                        }
                        secondary={
                          <Stack component="span" spacing={0.25} sx={{ mt: 0.5 }}>
                            <Typography component="span" variant="caption" color="text.secondary">
                              Slide {result.slideId}
                              {metadata ? ` | ${metadata}` : ''} | Heatmaps {result.heatmapCount} | Feature values{' '}
                              {result.featureValueCount}
                            </Typography>
                            {createdAt && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                Created {createdAt}
                              </Typography>
                            )}
                            <Typography component="span" variant="caption" color="text.secondary">
                              Approval:{' '}
                              {result.approved == null ? 'Unset' : result.approved ? 'Approved' : 'Not approved'}
                              {' | '}Internal:{' '}
                              {result.internallyApproved == null
                                ? 'Unset'
                                : result.internallyApproved
                                ? 'Approved'
                                : 'Not approved'}
                            </Typography>
                            {archivedAt && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                Archived {archivedAt}
                                {result.deletedBy ? ` by ${result.deletedBy}` : ''}
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
};
